import {
  Component,
  Inject,
  Input,
  OnChanges,
  Optional,
  SimpleChanges,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { ActiveOrder } from '@app/shared/CSDAModels/active-order.model';
import { ShipmentService } from '../../services/shipment.service';
import { SharedModule } from '@app/shared/shared.module';

@Component({
  selector: 'app-active-order-ccform',
  standalone: true,
  templateUrl: './active-order-ccform.component.html',
  styleUrls: ['./active-order-ccform.component.scss'],
  imports: [FormsModule, MatDialogModule, SharedModule],
})
export class ActiveOrderCCFormComponent implements OnChanges {
  @Input() orderNo!: number;

  order: ActiveOrder = {} as ActiveOrder;
  isProcessing = false;
  preserveOrderNo = false;
  packages: { PackageType: string; Qty: number; Size?: number }[] = [];

  constructor(
    private shipmentService: ShipmentService,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any,
  ) {
    if (data?.orderNo) {
      this.orderNo = data.orderNo;
      this.loadOrder(this.orderNo);
    }
  }

  get pickupFullAddress(): string {
    return `${this.order?.PickupCompanyName ?? ''} | ${this.order?.PickupStreet ?? ''} ${this.order?.PickupUnit ?? ''}`.trim();
  }

  get deliveryFullAddress(): string {
    return `${this.order?.DeliveryCompanyName ?? ''} | ${this.order?.DeliveryStreet ?? ''} ${this.order?.DeliveryUnit ?? ''}`.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderNo'] && this.orderNo) {
      this.loadOrder(this.orderNo);
    }
  }

  onSearchOrder(): void {
    if (!this.order?.OrderNo) return;
    this.preserveOrderNo = true;
    this.loadOrder(Number(this.order.OrderNo));
  }

  private loadOrder(orderNo: number): void {
    this.isProcessing = true;

    const typedOrderNo = this.order?.OrderNo;

    this.shipmentService
      .getCCOrderFormData(orderNo, this.preserveOrderNo)
      .subscribe((data) => {
        if (data.OrderDate)
          data.OrderDate = this.formatDateTime(data.OrderDate);

        if (data.PickupArrivalTime)
          data.PickupArrivalTime = this.formatDateTime(data.PickupArrivalTime);

        if (data.DeliveryArrivalTime)
          data.DeliveryArrivalTime = this.formatDateTime(
            data.DeliveryArrivalTime,
          );

        if (data.ReadyTime) {
          data.Pickup = data.ReadyTime.split('T')[0];
          data.ReadyTime = this.formatTimeOnly(data.ReadyTime);
        }

        if (data.DeliverBy) {
          data.Delivery = data.DeliverBy.split('T')[0];
          data.By = this.formatTimeOnly(data.DeliverBy);
        }

        // ✅ Build dynamic package list from strings
        if (data.PackageTypes && data.PackageQtys) {
          const types = data.PackageTypes.split(';');
          const qtys = data.PackageQtys.split(',');

          this.packages = types.map((t: string, i: number) => ({
            PackageType: t,
            Qty: Number(qtys[i] ?? 0),
            Size: 0,
          }));
        } else {
          this.packages = [];
        }

        this.order = data;

        if (this.preserveOrderNo) {
          this.order.OrderNo = typedOrderNo;
          this.preserveOrderNo = false;
        }

        this.isProcessing = false;
      });
  }

  private formatDateTime(value: string): string {
    const d = new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  }

  private formatTimeOnly(value: string): string {
    const d = new Date(value);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${min}`;
  }
}
